import { useEffect, useRef, useState } from "react";
import * as api from "../../lib/claimsApi.ts";
import type { FallacyResult } from "../../types/claims";

interface UseFallacyResult {
  fallacy: FallacyResult | null;
  isLoading: boolean;
  error: boolean;
}

/** Fetches the fallacy detection result for the given verification.
 *  Fires once when both userEventVerificationId and `enabled` are truthy. The caller
 *  passes enabled=false to skip the call (e.g. INSUFFICIENT verdict — nothing
 *  to ground a fallacy analysis on). Errors are surfaced as a flag, not thrown. */
export function useFallacy(
  userEventVerificationId: string | undefined,
  enabled: boolean
): UseFallacyResult {
  const [fallacy, setFallacy] = useState<FallacyResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!userEventVerificationId || !enabled || hasFetched.current) return;
    hasFetched.current = true;
    setIsLoading(true);
    api
      .fetchFallacy(userEventVerificationId)
      .then(setFallacy)
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [userEventVerificationId, enabled]);

  return { fallacy, isLoading, error };
}
